import {
  Component,
  OnDestroy,
  OnInit,
  HostListener,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
  TemplateRef,
} from '@angular/core';
import {
  DraftNote,
  VerticalUserInputs,
  HorizontalHeaders,
  AddtionalParams,
  SoapSetting,
} from '../../models/dictation.interface';
import { UserPreferenceService } from '../../services/user-preferences/user-preference.service';
import { CVA_CONSTANTS } from '../../util/constants';
import {
  SectionType,
  soapTemplateType,
  SubSectionTitle,
  TableType,
  VitalResponse,
} from '../../util/soap-config.constants';
import { NotificationClassEnum } from '../../models/notification-class.enum';
import { CVANotificationService } from '../../services/cva-notification/cva-notification.service';
import { SoapServiceService } from '../../services/soap-service/soap-service.service';
import { IAppointment } from '../../models/appointment.interface';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { environment } from '../../../environments/environment';
import { ConfirmDailogComponent } from '../confirm-dailog/confirm-dailog.component';
import { NavigationStart, Router } from '@angular/router';
import { ROUTE_CONSTANTS } from '../../util/route.constants';
import { Clipboard } from '@angular/cdk/clipboard';
import { SpeechToTextService } from '@privatelibdev/speech-to-text';
import { PractitionerService } from '../../services/practitioner/practitioner.service';
import { CVA_AUTH_MODULES } from '../../models/auth.modules.enum';
import { filter } from 'rxjs/internal/operators/filter';
import { debounceTime, Subject, takeUntil, finalize } from 'rxjs';
import { RtfEditorComponent } from '../../shared/rtf/rtf-editor/rtf-editor.component';
import { ITab, TemplateListing } from '../../models/manage-template.interface';
import { TemplateCategory } from '../../models/template.model';
import { FormControl } from '@angular/forms';
import { SessionInactivityService } from '../../services/session-inactivity/session-inactivity.service';
import { SpeechProvider, SttUIStatus } from '../../models/stt-chunked.model';
import { ScribeService } from '../../services/stt-chunked/scribe.service';
interface TempEdit {
  parentIndex: number;
  lastPointer: number;
  lastMessage: string;
  doNotUpdateOtherFields: boolean;
  type: SectionType;
  fieldName: string;
}

@Component({
  standalone: false,
  selector: 'cva-soap-notes',
  templateUrl: './soap-notes.component.html',
  styleUrl: './soap-notes.component.scss',

})
export class SoapNotesComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild('noteEditor') noteEditor?: RtfEditorComponent;
  @ViewChild('plainNote') plainNote?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('askAiDialog', { static: true }) askAiDialog?: TemplateRef<unknown>;
  private askAiDialogRef?: MatDialogRef<unknown>;
  readonly refinePrompt = new FormControl('', { nonNullable: true });
  askAiOpen = false;
  refining = false;
  refinementError = '';
  refinementPatterns: string[] = [];
  refinementPreview = '';
  refinementTarget = '';
  private refinementOriginal = '';
  private refinementContent = '';

  openAskAi(): void {
    if (this.askAiOpen) return;
    this.refinementOriginal = this.tabControl.value || '';
    const context = this.useRtfEditor ? this.noteEditor?.getRefinementContext() : null;
    const textarea = this.plainNote?.nativeElement;
    this.refinementContent = context?.content ?? this.refinementOriginal;
    this.refinementTarget = context?.target ?? (textarea
      ? this.refinementOriginal.slice(textarea.selectionStart, textarea.selectionEnd) : '');
    this.refinementError = '';
    this.refinementPatterns = [];
    this.refinementPreview = '';
    this.askAiOpen = true;
    if (this.askAiDialog) {
      this.askAiDialogRef = this.dialog.open(this.askAiDialog, {
        width: '720px',
        maxWidth: '95vw',
        maxHeight: '90dvh',
        disableClose: false,
        autoFocus: 'textarea',
        restoreFocus: true,
        ariaLabel: 'Refine your note',
      });
      this.askAiDialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.askAiOpen = false;
        this.askAiDialogRef = undefined;
      });
    }
  }

  closeAskAi(): void {
    if (this.refining) return;
    this.askAiDialogRef?.close();
    this.askAiOpen = false;
  }

  requestRefinement(): void {
    if (this.refining || !this.refinePrompt.value.trim() || this.disableApprove) return;
    if (this.tabControl.value !== this.refinementOriginal) {
      this.refinementError = 'The note changed. Close Refine with AI and select the text again.';
      return;
    }
    this.refining = true;
    if (this.askAiDialogRef) this.askAiDialogRef.disableClose = true;
    this.refinementError = '';
    this.refinementPatterns = [];
    this.refinementPreview = '';
    this.soapServiceService.refineNote({
      prompt: this.refinePrompt.value.trim(), target: this.refinementTarget, content: this.refinementContent,
    }).pipe(takeUntil(this.destroy$), finalize(() => {
      this.refining = false;
      if (this.askAiDialogRef) this.askAiDialogRef.disableClose = false;
    })).subscribe({
      next: ({ response }) => {
        if (response?.error) {
          this.refinementError = response.error;
          this.refinementPatterns = response.suspicious_patterns || [];
        } else if (response?.refined_content?.trim()) {
          this.refinementPreview = response.refined_content;
        } else {
          this.refinementError = 'Failed to refine the note.';
        }
      },
      error: (error) => {
        const detail = error?.error;
        this.refinementError = typeof detail?.error === 'string' ? detail.error
          : detail?.error?.errorMessage || 'Failed to refine the note.';
        this.refinementPatterns = Array.isArray(detail?.suspicious_patterns) ? detail.suspicious_patterns : [];
      },
    });
  }

  applyRefinement(): void {
    if (!this.refinementPreview || this.refining || this.disableApprove) return;
    if (this.tabControl.value !== this.refinementOriginal) {
      this.refinementError = 'The note changed. Close Refine with AI and try again to keep your latest edits.';
      return;
    }
    // The API returns the full note, even for a selected target. Escape plain
    // text before passing it to the rich text editor.
    const escaped = document.createElement('div');
    escaped.textContent = this.refinementPreview;
    const value = this.useRtfEditor
      ? '<p>' + escaped.innerHTML.replace(/\n/g, '<br>') + '</p>' : this.refinementPreview;
    this.tabControl.setValue(value);
    this.tabControl.markAsDirty();
    this.saveForm.emit();
    this.closeAskAi();
    this.refinementPreview = '';
    this.refinePrompt.reset();
  }

  @Input() appointment!: IAppointment;
  @Input() isViewMode: boolean = false;
  @Input() isTabActive: boolean = false;
  @Input() templateType: string = soapTemplateType.Lite;
  @Input() noteTemplateListing: TemplateListing[] = [];
  @Input() tabControl!: FormControl;
  @Input() vitals: VerticalUserInputs[] = [];
  @Input() disableApprove: boolean = false;
  @Input() selectedTemplate!: TemplateListing;
  @Input() tabIndex: number;
  @Input() DEFAULT_PREFERRED_TEMPLATE: string;
  @Input() hasDefaultTemplate: boolean;
  @Input() transcription: string = "";
  @Output() approve = new EventEmitter();
  @Output() saveForm = new EventEmitter();
  @Output() toggleTabDisable = new EventEmitter();
  @Output() updateTabIndex: EventEmitter<ITab> = new EventEmitter<ITab>()

  private readonly destroy$ = new Subject<void>();
  public TableTypeInt = TableType;
  TemplateCategory = TemplateCategory;
  public tempEdit: TempEdit = {
    fieldName: '',
    parentIndex: -1,
    type: SectionType.list,
    lastMessage: '',
    lastPointer: -1,
    doNotUpdateOtherFields: false,
  };
  cvaUI_url = environment.cvaUI_url;
  useRtfEditor = environment.enableRtfEditor;
  referenceId: string | undefined;
  CVA_CONSTANTS = CVA_CONSTANTS;
  CVA_AUTH_MODULES = CVA_AUTH_MODULES;
  isApproved = false;
  isApproving = false;
  isLoaded = false;
  routerSubcription$;
  PRIVACY_POLICY_ROUTE = ROUTE_CONSTANTS.PRIVACY_POLICY;
  recordingStarted = false;
  showApprovePopup: boolean = true;
  patientHistory: number = 0;

  selectedSpeechProvider: SpeechProvider = environment.sttService.defaultProvider;
  minRecordingSeconds: number = environment.sttService.audioChunks.minRecordingSecondsForNotesScreen;
  maxRecordingSeconds: number = environment.sttService.audioChunks.maxRecordingSecondsForNotesScreen;

  sttUIStatus: SttUIStatus = '';

  private pendingApproveRequests = 0;
  private approveSoapSaved = false;
  private readonly interimUpdatesEnabled = false;

  constructor(
    private readonly userPreferenceService: UserPreferenceService,
    private readonly cvaNotificationService: CVANotificationService,
    private readonly soapServiceService: SoapServiceService,
    public dialog: MatDialog,
    private readonly router: Router,
    private readonly clipboard: Clipboard,
    public speechToTextService: SpeechToTextService,
    private readonly practitionerService: PractitionerService,
    private readonly sessionInactivityService: SessionInactivityService,
    private readonly scribeService: ScribeService
  ) {
    this.routerSubcription$ = this.router.events?.pipe(filter((event) => event instanceof NavigationStart))
      .subscribe(() => {
        this.stopRecording();
      });
    this.referenceId = this.practitionerService?.loggedInPractitioner?.referenceId ??
      history.state?.practitioner?.referenceId;
  }

  ngOnInit() {
    if (!this.vitals) {
      this.vitals = [];
    }
    this.getTemplateSetting();
    this.getUserPreference();
    this.tabControl?.valueChanges
      .pipe(
        debounceTime(500) // Wait 500ms after last keystroke
      )
      .subscribe(() => {
        this.saveForm.emit();
      });

  }

  ngOnChanges(changes: SimpleChanges) {
    const result = this.soapServiceService.getTemplate(this.templateType, this.noteTemplateListing);
    if (result) {
      this.selectedTemplate = result.selectedTemplate;
    }
    if (changes['isTabActive']) {
      this.recordingStarted = false;
      this.toggleTabDisable.emit(false);
      this.stopRecording();
    }
  }

  setObjectFirstTime() {
    this.tempEdit.type = SectionType.list;
    this.tempEdit.lastPointer = this.tempEdit?.lastMessage.length;
    this.tempEdit.parentIndex = -1;
  }

  checkStartAndStop(event: string) {
    if (event === 'started') {
      this.sessionInactivityService.setSpeechInProgress(true);
      this.recordingStarted = true;
      if (this.tempEdit.lastPointer <= 0) {
        this.tempEdit.lastPointer = this.tabControl?.value.length;
      }
      this.tempEdit.lastMessage = this.tabControl?.value;
      this.createSelection(this.tempEdit.fieldName, SectionType.list, 500);
    } else if (event === 'stopped') {
      this.sessionInactivityService.setSpeechInProgress(false);
      this.toggleTabDisable.emit(false)
    }
  }

  recordedText(event: string) {
    if (this.isTabActive) {
      this.toggleTabDisable.emit(false)
      this.updateTextClassic(event, false).then(() => { });
    }
  }

  handleSoapResponse(soapSetting: SoapSetting): void {
    if (!soapSetting) return;
    if (!this.tabControl?.value) {
      this.tabControl.setValue("")
    }
    this.setObjectFirstTime();
    this.isLoaded = true;
  }

  async createObjectForUI() {
    const referenceId =
      this.practitionerService?.loggedInPractitioner?.referenceId ??
      history.state?.practitioner?.referenceId;
    this.userPreferenceService
      .getSoapSettings(referenceId, CVA_CONSTANTS.CONSTS.soap_setting)
      // eslint-disable-next-line  @typescript-eslint/no-explicit-any
      .subscribe((response: any) => {
        if (response) {
          this.handleSoapResponse(response.preference);
        }
      });
  }

  getUserPreference() {
    const referenceId =
      this.practitionerService?.loggedInPractitioner?.referenceId ??
      history.state?.practitioner?.referenceId;
    this.userPreferenceService
      .getSoapSettings(referenceId, CVA_CONSTANTS.CONSTS.user_preference)
      // eslint-disable-next-line  @typescript-eslint/no-explicit-any
      .subscribe((response: any) => {
        if (Object.hasOwn(response?.preference, 'approve_popup')) {
          this.showApprovePopup = response?.preference?.approve_popup
        }
        this.patientHistory = response?.preference?.patient_history || 0;
      });
  }


  updateRecordAsperType(
    key: SectionType,
    oldText: string,
    newText: string,
    addspace: boolean
  ): Promise<{ valid: boolean; newText: string }> {
    return new Promise((resolve) => {
      switch (key) {
        case SectionType.list:
          oldText = this.appendText(oldText, newText, addspace);
          if (addspace) {
            this.tempEdit.lastMessage = oldText;
            this.tempEdit.lastPointer += newText.length;
          }
          resolve({
            valid: true,
            newText: oldText,
          });
          break;
        case SectionType.date:
        case SectionType.number:
        case SectionType.text:
        case SectionType.dropdown: {
          if (newText.endsWith('.')) {
            oldText = newText.slice(0, -1);
          } else {
            oldText = newText;
          }
          this.tempEdit.lastMessage = oldText;
          this.tempEdit.lastPointer = this.tempEdit.lastMessage.length;
          resolve({
            valid: true,
            newText: oldText,
          });
          break;
        }
      }
    });
  }

  async backspaceClassic() {
    if (this.tempEdit.lastPointer > 0 && this.tempEdit.fieldName === ('classic-txt' + this.tabIndex)) {
      const second = this.tempEdit.lastMessage.slice(this.tempEdit.lastPointer);
      this.tempEdit.lastPointer -= 1;
      const first = this.tempEdit.lastMessage.slice(
        0,
        this.tempEdit.lastPointer
      );
      this.tabControl.setValue(first + second);
      this.tempEdit.lastMessage = this.tabControl?.value;
      await this.createSelection(this.tempEdit.fieldName, SectionType.list, 0);
    } else {
      this.updateTextClassic('', false);
    }
  }


  updateTextClassic(newText: string, isRecognizing: boolean) {
    if (this.useRtfEditor && this.noteEditor && this.tempEdit.type === SectionType.list) {
      if (this.recordingStarted && !isRecognizing) {
        this.noteEditor.insertSpeechText(newText);
      }
      return Promise.resolve(true);
    }
    return new Promise((resolve) => {
      const promiseResult: Promise<{ valid: boolean; newText: string }> = this.updateRecordAsperType(this.tempEdit.type,
        this.tempEdit.lastMessage,
        newText,
        !isRecognizing);

      promiseResult.then(async (temp) => {
        if (temp.valid) {
          if (this.tempEdit.type === SectionType.list) {
            if (this.recordingStarted) {
              this.tabControl.setValue(temp.newText);

              await this.createSelection(
                this.tempEdit.fieldName,
                this.tempEdit.type,
                0
              );
            }
            resolve(true);
          } else if (temp.newText) {
            this.validateInput(temp.newText, this.tempEdit.fieldName, this.vitals[this.tempEdit.parentIndex]).then(
              (result) => {
                if (result.isValid) {
                  this.vitals[this.tempEdit.parentIndex].value = temp.newText;
                  this.moveRowVitals(this.vitals, 1)
                }
              }
            );
          }
          resolve(true);
        } else {
          //reset value if need
          resolve(true);
        }
      });
    });
  }

  async moveRowVitals(temp: Array<VerticalUserInputs>, moveBy: number, timer: number = 0) {
    if (temp[this.tempEdit.parentIndex + moveBy]) {
      this.tempEdit.parentIndex += moveBy;
      this.tempEdit.fieldName = temp[this.tempEdit.parentIndex].title;
      this.tempEdit.lastMessage = temp[this.tempEdit.parentIndex].value;
      this.tempEdit.lastPointer = this.tempEdit.lastMessage.length;
      this.tempEdit.type = temp[this.tempEdit.parentIndex].type;
      await this.createSelection(
        this.tempEdit.fieldName,
        temp[this.tempEdit.parentIndex].type,
        timer
      );
    }
  }

  handleIntent(command: string, newText: string, sectionName: string) {
    if (!this.recordingStarted) {
      return;
    }
    if (['moveToPreviousField', 'moveToNextField', 'removeSection', 'addSection', 'selectSection'].indexOf(command) > -1) {
      this.updateTextClassic('', false).then(() => { });
      return;
    }
    switch (command) {
      case 'backspace':
        this.backspaceClassic();
        break;
      case 'clearField':
        this.tempEdit.lastMessage = ""
        this.tempEdit.lastPointer = 0;
        if (this.tempEdit.fieldName === ('classic-txt' + this.tabIndex)) {
          this.tabControl.setValue("")
        } else if (this.tempEdit.parentIndex > -1) {
          this.vitals[this.tempEdit.parentIndex].value = "";
        }
        break;
      case 'bullet':
        this.updateTextClassic('•', false).then(() => { });
        break;
      case 'openPopUp':
        this.updateTextClassic('', false).then(() => {
          this.soapServiceService.openHistoryPopUp$.next(sectionName);
        });
        break;
      case 'closePopUp':
        this.updateTextClassic('', false).then(() => {
          this.soapServiceService.closeHistoryPopUp$.next('close');
        });
        break;
      default:
        this.updateTextClassic(newText, false).then(() => { });
        break;
    }

  }

  recognizingText(event: string) {
    if (!this.interimUpdatesEnabled || !this.isTabActive) return;
    this.toggleTabDisable.emit(true);
    if (this.tempEdit.type === SectionType.list) {
      this.updateTextClassic(event, true);
    }
  }

  appendText(oldText: string, newText: string, addSpacePointer: boolean): string {
    if (this.tempEdit.lastPointer <= -1) {
      throw new Error('Please select particular section.');
    }

    if (newText === '') {
      return oldText;
    }

    const firstPart = oldText.slice(0, this.tempEdit.lastPointer);
    const secondPart = oldText.slice(this.tempEdit.lastPointer);
    const isSpace = this.isLastCharSpace(firstPart);

    let temp = '';

    if (this.isBulletOrNewline(newText) && firstPart) {
      const processed = this.handleBulletOrNewline(firstPart, newText, isSpace);
      if (processed === null) {
        return oldText;
      }
      temp = processed;
    } else if (newText !== '\n') {
      temp = this.handleNormalText(firstPart, newText, isSpace, addSpacePointer);
    } else {
      this.tempEdit.lastPointer -= 1;
      return oldText;
    }

    if (secondPart) {
      temp += secondPart.trim();
    }

    return temp;
  }

  private isBulletOrNewline(text: string): boolean {
    return ['•', '\n'].includes(text);
  }

  private handleBulletOrNewline(firstPart: string, newText: string, isSpace: boolean): string | null {
    let temp = firstPart.trim();
    const lastChar = temp.slice(-1);

    if (lastChar === '\n' && newText === '\n') {
      if (isSpace) {
        this.tempEdit.lastPointer -= 1;
      }
      this.tempEdit.lastPointer -= 1;
      return null;
    }

    if (
      (lastChar === '•' && newText === '•') ||
      (lastChar === '•' && newText === '\n')
    ) {
      this.tempEdit.lastPointer -= 1;
      return null;
    }

    if (!['.', ',', '?', '!', '-', ';'].includes(lastChar)) {
      temp += '.';
      this.tempEdit.lastPointer += 1;
    }

    if (newText === '•') {
      temp += `\n${newText} `;
      if (!isSpace) {
        this.tempEdit.lastPointer += 1;
      }
    } else {
      temp += `${newText}`;
      if (isSpace) {
        this.tempEdit.lastPointer -= 1;
      }
    }

    return temp;
  }

  private handleNormalText(
    firstPart: string,
    newText: string,
    isSpace: boolean,
    addSpacePointer: boolean
  ): string {
    let temp = firstPart;
    const lastChar = temp.slice(-1);

    if (['\n'].includes(lastChar) || isSpace || this.tempEdit.lastPointer === 0) {
      temp += `${newText} `;
    } else {
      temp += ` ${newText} `;
    }

    if (addSpacePointer) {
      this.tempEdit.lastPointer += 1;
    }

    return temp;
  }


  isLastCharSpace(text: string): boolean {
    return text?.endsWith(' ');
  }

  assignPointer($event: MouseEvent | KeyboardEvent) {
    const target = $event.target as HTMLInputElement;
    this.tempEdit.lastPointer = target.selectionStart as number;
  }

  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  clikedClassicNotes($event: any) {
    if ($event?.target?.selectionStart && $event?.target?.selectionEnd) {
      if ($event.target?.selectionStart == $event.target?.selectionEnd) {
        this.tempEdit.lastMessage = this.tabControl?.value;
        this.tempEdit.fieldName = "classic-txt" + this.tabIndex;
        this.tempEdit.type = SectionType.list;
        this.tempEdit.parentIndex = -1;
        this.assignPointer($event);
        this.createSelection(this.tempEdit.fieldName, SectionType.list, 0);
      }
    }
  }

  shortInputKeyUp($event: KeyboardEvent) {
    if ($event?.target) {
      this.tempEdit.lastMessage = ($event.target as HTMLInputElement).value;
      this.assignPointer($event);
    }
  }

  createSelection(attributeId: string, type: SectionType, timer: number) {
    return new Promise((resolve) => {
      setTimeout(() => {
        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        const input: any = document.getElementById(attributeId);
        if (input) {
          if (type === SectionType.list && this.tempEdit.lastPointer > -1) {
            input.setSelectionRange(
              this.tempEdit.lastPointer,
              this.tempEdit.lastPointer
            );
          }
          if (type === SectionType.dropdown) {
            input.children[0].focus();
          } else {
            input.focus();
          }
        }
        resolve(true);
      }, timer);
    });
  }

  approveNotes() {
    if (!this.showApprovePopup) {
      this.approveClassicNotes();
      return;
    }
    const dialogRef = this.dialog.open(ConfirmDailogComponent, {
      width: '300px',
      data: {
        title: 'Approve Consultation Notes',
      },
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.approveClassicNotes()
      }
    });
  }

  approveClassicNotes() {
    if (this.tabControl?.value?.trim()) {
      this.isApproving = true;
      this.pendingApproveRequests = 0;
      this.approveSoapSaved = false;

      let subjective = JSON.parse(JSON.stringify(this.tabControl?.value || ''));

      // eslint-disable-next-line  @typescript-eslint/no-explicit-any
      const temp: any = {
        vitalDate: new Date().toISOString(),
        practitionerId: this.appointment.practitioner.referenceId,
        patientId: this.appointment.patient.referenceId
      }
      let vitalText = "";
      temp.vitals = [];
      this.vitals.forEach((input) => {
        if (input.value) {
          const separator = this.useRtfEditor
            ? '<br>'
            : '\n';

          vitalText += `${input.title} (${input.unit}): ${input.value}${separator}`;
          temp.vitals.push({
            key: input.title,
            value: input.value
          })
        }
      })
      if (temp.vitals.length > 0) {
        this.beginApproveRequest();
        this.soapServiceService.saveVitals(temp).subscribe({
          next: (data: { "response": VitalResponse }) => {
            if (data.response?.failures.length > 0) {
              this.cvaNotificationService.showNotification(
                "Error while saving " + data.response.failures.join(', '),
                NotificationClassEnum.ERROR
              );
            } else {
              this.cvaNotificationService.showNotification(
                "Vitals saved.",
                NotificationClassEnum.SUCCESS
              );
            }
            this.endApproveRequest();
          },
          error: () => {
            this.cvaNotificationService.showNotification(
              CVA_CONSTANTS.MESSAGE.save_err_vitals,
              NotificationClassEnum.ERROR
            );
            this.endApproveRequest();
          }
        })

        if (vitalText) {
          const vitalPrefix = this.useRtfEditor
            ? '<br>VITAL SIGNS:<br>'
            : '\n\nVITAL SIGNS:\n';

          subjective += `${vitalPrefix}${vitalText}`;
        }
      }
      this.beginApproveRequest();
      this.soapServiceService
        .saveSoapNotes({
          appointmentId: this.appointment.referenceId,
          tempUid: this.appointment.uid,
          subjective: subjective,
          objective: "",
          assessment: "",
          plan: "",
          patientId: this.appointment.patient.referenceId,
          soapNoteDate: new Date().toISOString(),
          practitionerId: this.appointment.practitioner.referenceId,
          templateType: this.templateType,
          isCustomTemplate: this.selectedTemplate?.isCustomTemplate || false,
        })
        .subscribe({
          next: () => {
            this.approveSoapSaved = true;
            this.cvaNotificationService.showNotification(
              CVA_CONSTANTS.MESSAGE.save_soap_notes,
              NotificationClassEnum.INFO
            );
            this.endApproveRequest();
          },
          error: () => {
            this.approveSoapSaved = false;
            this.cvaNotificationService.showNotification(
              CVA_CONSTANTS.MESSAGE.save_err_soap_notes,
              NotificationClassEnum.ERROR
            );
            this.endApproveRequest();
          },
        });
    } else {
      this.isApproving = false;
      this.cvaNotificationService.showNotification(
        CVA_CONSTANTS.MESSAGE.empty_soap_notes_classic,
        NotificationClassEnum.ERROR
      );
    }
  }

  private beginApproveRequest() {
    this.pendingApproveRequests += 1;
  }

  private endApproveRequest() {
    this.pendingApproveRequests = Math.max(0, this.pendingApproveRequests - 1);
    if (this.pendingApproveRequests === 0) {
      this.isApproving = false;
      if (this.approveSoapSaved) {
        this.isApproved = true;
        this.approve.emit();
        this.approveSoapSaved = false;
      }
    }
  }

  stopRecording() {
    this.speechToTextService.stopRec.next('stopped');
    if (this.selectedSpeechProvider === 'audio-chunks') {
      this.scribeService.stopRec.next('stopped');
    }
  }

  copyToClipboard(textareaId: string) {
    const element = document.getElementById(textareaId) as HTMLTextAreaElement | HTMLInputElement | null;
    const elementValue = element && 'value' in element ? element.value : '';
    const controlValue = this.tabControl?.value || '';
    const textToCopy = this.getClipboardText(elementValue || controlValue);

    if (textToCopy) {
      this.clipboard.copy(textToCopy);
      this.cvaNotificationService.showNotification(CVA_CONSTANTS.MESSAGE.COPY_SUCCESS, NotificationClassEnum.SUCCESS, 3000);
    }
  }

  private getClipboardText(value: string): string {
    if (!value) {
      return '';
    }

    if (!/<[a-z][\s\S]*>/i.test(value)) {
      return value;
    }

    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = value
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>\s*<p[^>]*>/gi, '\n\n')
      .replace(/<\/div>\s*<div[^>]*>/gi, '\n');

    return (tempContainer.textContent || '').trim();
  }

  stripSpecialCharacter(text: string | undefined) {
    if (!text) {
      return '';
    }
    const regex = CVA_CONSTANTS.REGEX.specialCharacter;
    const isSpecialChar = regex.test(text);

    if (text && text.length > 0 && isSpecialChar) {
      text = text.substring(0, text.length - 1);
    }
    return text;
  }

  validateInput(
    msgString: string,
    fieldName: string,
    attribute: HorizontalHeaders
  ): Promise<{ isValid: boolean; newText?: string }> {
    return new Promise((resolve) => {
      const msgStr = msgString.trim();
      if (!msgStr) {
        return resolve({ isValid: true });
      }

      switch (attribute?.type) {
        case SectionType.number:
          return this.validateNumberField(msgStr, attribute, resolve);

        case SectionType.dropdown:
          return this.validateDropdownField(msgStr, attribute, resolve);

        default:
          return this.validateOtherFields(msgStr, attribute, resolve);
      }
    });
  }

  private validateNumberField(
    msgStr: string,
    attribute: HorizontalHeaders,
    resolve: (value: { isValid: boolean; newText?: string }) => void
  ) {
    msgStr = msgStr.replace(/\.$/, '');
    const match = msgStr.match(/^\d*$/gm);

    if (match) {
      const number = parseInt(match[0]);
      if (attribute.title === 'Age' && (number < 0 || number > 100)) {
        this.notifyError(CVA_CONSTANTS.MESSAGE.AGE_LIMIT_ERROR);
        return resolve({ isValid: false });
      }
    } else {
      const field = attribute.title.replace(')', '').split('(')[0];
      this.notifyError(`Only numeric values are allowed for ${field}`);
      return resolve({ isValid: false });
    }

    return resolve({ isValid: true });
  }

  private validateDropdownField(
    msgStr: string,
    attribute: HorizontalHeaders,
    resolve: (value: { isValid: boolean; newText?: string }) => void
  ) {
    const enums = attribute?.data || [];
    const cleanedInput = msgStr.toLowerCase().replace(/\.$/, '');

    const isValid = enums.some(option =>
      option.trim().toLowerCase() === cleanedInput
    );

    if (!isValid) {
      const field = attribute.title.replace(')', '').split('(')[0];
      this.notifyError(`The value of ${field} is invalid.`);
      return resolve({ isValid: false });
    }

    return resolve({ isValid: true });
  }

  private validateOtherFields(
    msgStr: string,
    attribute: HorizontalHeaders,
    resolve: (value: { isValid: boolean; newText?: string }) => void
  ) {
    const title = attribute?.title;

    if (title === 'Blood Pressure') {
      const cleanedBP = this.cleanBloodPressure(msgStr);
      const isNumber = /^([\d/ ]*)$/.test(cleanedBP);
      const slashCount = (cleanedBP.match(/\//g) || []).length;
      const validFormat = /^\d+\/\d+$/.test(cleanedBP.trim());

      if (!isNumber || slashCount !== 1 || !validFormat) {
        this.notifyError(CVA_CONSTANTS.MESSAGE.INVALID_BP);
        return resolve({ isValid: false });
      }

      return resolve({ isValid: true, newText: cleanedBP });
    }

    if (this.isVitalField(title)) {
      msgStr = msgStr.replace(/\.$/, '').replace(/dot|point/gi, '.');
      const isNumber = /^\d+(\.\d+)?$/.test(msgStr);

      if (!isNumber) {
        this.notifyError(CVA_CONSTANTS.MESSAGE.INVALID_VITAL + title);
        return resolve({ isValid: false });
      }

      return resolve({ isValid: true });
    }

    if (['allergen', 'medication name'].includes(title.toLowerCase())) {
      const cleaned = this.stripSpecialCharacter(attribute.value) || '';
      return resolve({ isValid: true, newText: cleaned });
    }

    return resolve({ isValid: true });
  }

  private cleanBloodPressure(bp: string): string {
    return bp
      .replace(/:/g, '')
      .replace(/\./g, '')
      .replace(/ /g, ' ')
      .toLowerCase()
      .replace(/over|by|slash/gi, '/')
      .replace(/\s+/g, '');
  }

  private isVitalField(title: string): boolean {
    const vitalFields = [
      'Temperature', 'Heart Rate', 'Oxygen', 'Height', 'Weight',
      'Body Mass Index', 'Respiratory Rate', 'Alcohol Cessation', 'Smoking Cessation'
    ];
    return vitalFields.includes(title);
  }

  private notifyError(message: string) {
    this.cvaNotificationService.showNotification(message, NotificationClassEnum.ERROR);
  }


  /**
   * get draft note
   * @returns DraftNote
   * -iterate over the subsections and if found value either in notes or table,
   *  just add notes & user input into the object
   */
  getDraftNote(): DraftNote {
    if (this.isApproved) return {};
    return {
      [this.templateType]: {
        section: {
          type: TableType.vertical,
          name: SubSectionTitle.VITAL_SIGNS,
          header: SubSectionTitle.VITAL_SIGNS,
        },
        notes: this.tabControl?.value as string,
        userInputs: this.vitals
      }
    }
  }

  /**
   * handle the draft note on component exit
   * -on approved, remove appointmentRefId from storage
   * -otherwise, store the appointmentRefId & notes
   */
  handleDraftNoteOnComponentExit() {
    if (this.appointment?.referenceId) {
      this.soapServiceService.setAppointmentAndDraftedNotes(
        this.appointment.referenceId,
        this.getDraftNote(),
        this.soapServiceService.getAppointmentAndDraftedNotes()?.transcription,
        this.templateType)
    }
  }

  getTemplateSetting() {
    if (this.referenceId) {
      this.createObjectForUI();
    }
  }

  editvalue(index: number) {
    const vital = this.vitals[index];
    this.tempEdit.fieldName = vital.title;
    this.tempEdit.type = vital.type;
    this.tempEdit.parentIndex = index;
    this.tempEdit.lastPointer = vital.value.length;
    this.createSelection(this.tempEdit.fieldName, vital.type, 100);
  }

  clearValue(index: number) {
    this.vitals[index].value = "";
  }

  async checkVitalInput(index: number) {
    const vital = this.vitals[index];
    if (vital.value !== "") {
      const result = await this.validateInput(vital.value, "", vital);
      if (result?.isValid === false) {
        this.clearValue(index);
      } else {
        this.saveForm.emit();
      }
    }
  }

  selectTextCustom(event: { codes: string; additionalParams: AddtionalParams }) {
    this.vitals[event.additionalParams.index].value = event.codes;
    this.saveForm.emit();
  }

  valueSelectedVitals(event: AddtionalParams) {
    this.editvalue(event.index)
  }

  generateMore(addNewTab: ITab) {
    this.updateTabIndex.emit(addNewTab)
  }

  onSpeechStatus(status: 'listening' | 'processing' | '') {
    this.sttUIStatus = status;
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.handleDraftNoteOnComponentExit();
  }

  ngOnDestroy(): void {
    this.askAiDialogRef?.close();
    this.routerSubcription$?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
    this.recordingStarted = false;
    this.toggleTabDisable.emit(false)
  }
}
